import React from 'react'
import { useQueries } from '@tanstack/react-query'
import { dayjs } from '@mobi/utils/date'
import { setShouldAllowPendingRacingTicketCountPolling } from '@core/Areas/AppHeader/Store'
import { selectShouldAllowPendingRacingTicketCountPolling } from '@core/Areas/AppHeader/Store/selectors'
import { kambiPendingBetHistoryQuery } from '@core/Areas/KambiSports/Player/helpers/queries'
import { queryClient } from '@core/Data/ReactQuery'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { store } from '@core/Store'
import { useAppSelector } from '@core/Store/hooks'
import { fetchRacePendingTicketsCount } from './helpers/api'
import { racingPendingTicketsPollingQuery } from './helpers/queries'
import type { PendingBetsCountHookReturn, RenderCount } from './types'

const enum LocalConstants {
  DefaultMaxCount = 99,
}

export const PendingTicketsCountProvider: React.FC<{
  render: RenderCount
  shouldForceRefetchPendingCount?: boolean
}> = ({ render, shouldForceRefetchPendingCount }) => {
  const isRacingPollingEnabled = useAppSelector(selectShouldAllowPendingRacingTicketCountPolling)

  const [
    {
      data: racing,
      isError: isRacingError,
      isLoading: isRacingLoading,
      isSuccess: isRacingSuccess,
      dataUpdatedAt: racingDataUpdatedAt,
    },
    { data: kambiData, isError: isKambiError, isLoading: isKambiLoading },
  ] = useQueries({
    queries: [
      { ...racingPendingTicketsPollingQuery, enabled: isRacingPollingEnabled },
      kambiPendingBetHistoryQuery,
    ],
  })

  const kambiPendingCount = kambiData?.range.size ?? 0

  React.useEffect(() => {
    if (shouldForceRefetchPendingCount && dayjs().diff(racingDataUpdatedAt, 'seconds') > 15) {
      queryClient.refetchQueries({ queryKey: queryKeys.pendingTicketsCountBase, type: 'active' })
      queryClient.refetchQueries({ queryKey: kambiPendingBetHistoryQuery.queryKey, type: 'active' })
    }
  }, [shouldForceRefetchPendingCount, racingDataUpdatedAt])

  React.useEffect(() => {
    if (isRacingSuccess) {
      const isCountAboveZero = !!(
        racing?.pendingTicketsCount && (racing.pendingTicketsCount ?? 0) > 0
      )
      store.dispatch(setShouldAllowPendingRacingTicketCountPolling(isCountAboveZero))
    }
  }, [isRacingSuccess, racing])

  return render({
    ...mapResponseToCountReturn({ racing, kambi: kambiPendingCount }),
    isRacingError,
    isRacingLoading,
    isKambiError,
    isKambiLoading,
  })
}

// =============
// Local Helpers
// =============

function mapResponseToCountReturn(data: {
  racing?: Awaited<ReturnType<typeof fetchRacePendingTicketsCount>>
  kambi?: number
}): Pick<
  PendingBetsCountHookReturn,
  'pendingBetsCount' | 'pendingRacingCount' | 'pendingSportsCount' | 'doesCountExceedMax'
> {
  const pendingRacingCount = data.racing?.pendingTicketsCount ?? null
  const pendingSportsCount = data.kambi ?? null

  const pendingBetsCount =
    pendingRacingCount !== null && pendingSportsCount !== null
      ? Math.min(pendingRacingCount + pendingSportsCount, LocalConstants.DefaultMaxCount)
      : null

  const doesCountExceedMax = !!(
    pendingBetsCount && pendingBetsCount > LocalConstants.DefaultMaxCount
  )

  return { pendingBetsCount, pendingRacingCount, pendingSportsCount, doesCountExceedMax }
}
